import { z } from "zod";
import { median } from "./interpret/series.js";
import { formatTemperatureC } from "./units.js";
/**
 * A finding is a comparison the member's own days can settle: split their days
 * by one reading, compare a second reading across the two halves, and report
 * the difference with how many days sat in each arm. It is never a claim about
 * people in general, and it is withheld until there are enough days to make it.
 */
export const MIN_FINDING_DAYS = 21;
export const MIN_FINDING_ARM_DAYS = 8;
/** Below this the two arms are the same days told twice, so nothing is shown. */
export const MIN_FINDING_R = 0.2;
export const snapshotFindingArmSchema = z.object({
    label: z.string(),
    value: z.string(),
    days: z.number().int(),
});
export const snapshotFindingSchema = z.object({
    id: z.string(),
    claim: z.string(),
    /**
     * Two or three words naming the pairing, for a surface with no room for the
     * sentence. The memory map puts the label under a node next to a dozen
     * others: "Your deep sleep and your heart rate variability that night"
     * overlaps its neighbours and reads as noise there, however well it reads
     * on the card.
     */
    shortClaim: z.string(),
    low: snapshotFindingArmSchema,
    high: snapshotFindingArmSchema,
    /** The gap between the arms, worded in the outcome's own units. */
    difference: z.string(),
    /**
     * The same gap with no direction word ("893 steps"), for a caller that needs
     * a value chip rather than a sentence. The memory graph sizes a node to fit
     * this text inside the disc, so a phrase there makes a finding look five
     * times more important than a fact next to it for no reason but its length.
     */
    magnitude: z.string(),
    days: z.number().int(),
    /** Pearson correlation across the paired days, rounded to two places. */
    r: z.number(),
    /**
     * The gap between the arms as a share of the reading itself, so a finding in
     * milliseconds can be ranked against one in beats per minute. 5 bpm on a
     * resting rate around 60 is a smaller change than 10 ms on an HRV around 26,
     * and the raw numbers say the opposite.
     */
    impact: z.number(),
    /** The 95% interval on the gap between the arms, in the outcome's units. */
    interval: z.string().nullable(),
    needs: z.array(z.string()),
    /** The payload fields each arm was read from, so a reader can check us. */
    reads: z.array(z.string()),
    note: z.string(),
    /** The one thing to do about it. A finding that asks nothing is a fact, not guidance. */
    move: z.string(),
    /**
     * Where the member stands on the exposure right now, and whether the move is
     * for them today. "What to do" without "and today is one of those days" asks
     * them to hold a rule and check it themselves.
     */
    now: z
        .object({
        label: z.string(),
        /** True when the run still sits on the side the move addresses. */
        applies: z.boolean(),
        /**
         * The run is where it should be and the newest day is not. A stumble is
         * where people stop, so the card names the day and says the week is
         * still there rather than letting one number read as a relapse.
         */
        dipped: z.boolean(),
        /** True where a worse day is a higher number: sitting, desk time, meetings. */
        worseIsHigher: z.boolean(),
        /** How many days the run was read over. */
        days: z.number().int(),
    })
        .nullable()
        .optional(),
    /** The drill this explains, so each tab can show only what bears on it. */
    drill: z.enum(["sleep", "recovery", "strain", "body"]),
    /** Whether the move lands on a day or on a night, which is the only timing a rule can honestly promise. */
    cadence: z.enum(["night", "day"]),
    /** The reading this splits on, so two findings cannot grade the same one both ways. */
    exposure: z.string(),
});
/** The payload field behind each series, cited by a finding that uses it. */
/** The drill each series is read on, so a finding sits with what it explains. */
const SERIES_DRILL = {
    steps: "strain",
    activeMinutes: "strain",
    calendarMinutes: "strain",
    rainMm: "strain",
    deskMinutes: "strain",
    sedentaryMinutes: "strain",
    sleepHours: "sleep",
    deepHours: "sleep",
    wakeDrift: "sleep",
    nightTempC: "sleep",
    hrv: "recovery",
    rhr: "recovery",
    stressMinutes: "recovery",
};
export const SNAPSHOT_FINDING_READS = {
    steps: "wearable.activity.steps",
    sleepHours: "wearable.sleep.hours",
    wakeDrift: "wearable.sleep.wake_time_variance_min",
    hrv: "wearable.hrv.rmssd_ms",
    rhr: "wearable.rhr.bpm",
    deskMinutes: "user.desk_session.durationMinutes",
    sedentaryMinutes: "wearable.activity.inactiveSeconds",
    calendarMinutes: "calendar.event.durationMinutes",
    deepHours: "wearable.sleep.stages.deepHours",
    activeMinutes: "wearable.activity.activeSeconds",
    stressMinutes: "wearable.stress.high_stress_seconds",
    nightTempC: "environment.weather_today.week.minC and environment.weather_archive.week.minC",
    rainMm: "environment.weather_today.week.precipMm and environment.weather_archive.week.precipMm",
    // Day length is not here on purpose. Over a ninety day window it correlates
    // with the date at r = -0.99, so pairing anything against it reads a seasonal
    // drift back as a result the member could act on.
};
function mean(values) {
    return values.reduce((a, b) => a + b, 0) / values.length;
}
function correlation(xs, ys) {
    const mx = mean(xs);
    const my = mean(ys);
    let num = 0;
    let dx = 0;
    let dy = 0;
    for (let i = 0; i < xs.length; i++) {
        const a = (xs[i] ?? 0) - mx;
        const b = (ys[i] ?? 0) - my;
        num += a * b;
        dx += a * a;
        dy += b * b;
    }
    const den = Math.sqrt(dx * dy);
    return den === 0 ? 0 : num / den;
}
/**
 * Pairs the two series on the days both reported, splits at the exposure's own
 * median, and returns the finding only when each arm carries enough days to be
 * worth reading. Returns null rather than a weak claim.
 */
/**
 * Where the member has been lately, and whether the move is one they still
 * need. `expect` names the side the outcome is better on, so the move is for
 * them when the run sits on the other one.
 *
 * The run is the median of the last week rather than the newest reading. One
 * quiet day is not a habit, and reading it as one turns an off Tuesday into a
 * correction the member has not earned.
 */
const NOW_RUN_DAYS = 7;
/**
 * The day a reading belongs to, in the member's words. Worked out from the key
 * rather than written into each spec: a cumulative exposure has today's
 * running total dropped before it reaches here, so its newest reading is
 * yesterday's finished one and calling it "today" dates it wrong.
 */
function dayWord(key, todayKey, cadence) {
    if (!todayKey)
        return "";
    const shift = (from, days) => {
        const d = new Date(`${from}T12:00:00Z`);
        d.setUTCDate(d.getUTCDate() - days);
        return d.toISOString().slice(0, 10);
    };
    const ago = key === todayKey ? 0 : key === shift(todayKey, 1) ? 1 : 2;
    if (cadence === "day")
        return ago === 0 ? " today" : ago === 1 ? " yesterday" : "";
    /*
     * A night is stamped at onset, so last night keys to yesterday for a bedtime
     * before midnight and to today for one after it. Both are the same night,
     * and `windowLabel` on the same drill already collapses them, so reading the
     * key literally had the card say "two nights ago" under a heading that said
     * "Last night".
     */
    if (ago <= 1)
        return " last night";
    return key === shift(todayKey, 2) ? " two nights ago" : "";
}
function nowFrom(spec, exposure, split, todayKey, units) {
    const latest = exposure.at(-1);
    if (!latest)
        return null;
    const run = exposure.slice(-NOW_RUN_DAYS).map((d) => d.value);
    const typical = median(run) ?? latest.value;
    const worseSide = (v) => (spec.expect === "higher" ? v <= split : v > split);
    const applies = worseSide(typical);
    return {
        label: `${spec.whenLabel(spec.format(latest.value, units))}${dayWord(latest.key, todayKey, spec.cadence)}`,
        applies,
        dipped: !applies && worseSide(latest.value),
        // Worse means lower for steps and sleep, higher for sitting, desk time,
        // meetings and a warm room. The card cannot call every off day quiet.
        worseIsHigher: spec.expect === "lower",
        days: run.length,
    };
}
/** How big the gap is next to the reading, which makes two units comparable. */
function relativeGap(lowMean, highMean) {
    const middle = Math.abs(lowMean + highMean) / 2;
    if (middle === 0)
        return 0;
    return Math.round((Math.abs(highMean - lowMean) / middle) * 1000) / 1000;
}
export function buildFinding(spec, exposure, outcome, needs, reads = [], todayKey, 
/*
 * Where the member stands is read off the exposure's own days, not the
 * lagged pairing series. A lag-1 pair re-keys each night to the morning it
 * is paired with, which leaves the newest night with no next-day key at all,
 * so it was dropped and the card presented the night before as current.
 */
standing = exposure, units = null) {
    const outcomeByKey = new Map(outcome.map((d) => [d.key, d.value]));
    const pairs = exposure
        .map((d) => ({ x: d.value, y: outcomeByKey.get(d.key) }))
        .filter((p) => typeof p.y === "number");
    if (pairs.length < MIN_FINDING_DAYS)
        return null;
    /*
     * The median is the split, but a reading with few distinct values can put the
     * median on the boundary itself, and one arm then holds every day. Trying the
     * boundary both ways keeps a real comparison; when neither balances, there is
     * no comparison to make and the finding is withheld.
     */
    const split = spec.splitAt ?? median(pairs.map((p) => p.x)) ?? 0;
    const arms = [
        { low: pairs.filter((p) => p.x <= split), high: pairs.filter((p) => p.x > split) },
        { low: pairs.filter((p) => p.x < split), high: pairs.filter((p) => p.x >= split) },
    ]
        .filter((a) => a.low.length >= MIN_FINDING_ARM_DAYS && a.high.length >= MIN_FINDING_ARM_DAYS)
        .sort((a, b) => Math.abs(a.low.length - a.high.length) - Math.abs(b.low.length - b.high.length));
    const balanced = arms[0];
    if (!balanced)
        return null;
    const { low, high } = balanced;
    const lowMean = mean(low.map((p) => p.y));
    const highMean = mean(high.map((p) => p.y));
    // Graded on which side of the split a day fell: over a skewed scale a few
    // extremes decide it. A real wet-day effect measured 0.03 against -0.19.
    const strength = spec.splitAt === undefined ? pairs.map((p) => p.x) : pairs.map((p) => (p.x > split ? 1 : 0));
    const r = Math.round(correlation(strength, pairs.map((p) => p.y)) * 100) / 100;
    if (Math.abs(r) < MIN_FINDING_R)
        return null;
    // Means and gap round separately, so identical arms can carry a worded gap.
    if (spec.formatOutcome(lowMean) === spec.formatOutcome(highMean))
        return null;
    /*
     * Pairings are ranked on the size of a correlation and not its sign, so a
     * member whose days run the other way would be told to do the thing their own
     * numbers associate with the worse outcome. Withheld here rather than muted
     * later: only four findings are shown, and a pairing that has nothing to ask
     * of this member should not take one of the four from a pairing that does.
     */
    const direction = highMean > lowMean ? "higher" : "lower";
    // Both the arm means and the correlation have to agree with the advice. They
    // can disagree: a handful of extreme exposure days can pull Pearson one way
    // while the arm means sit the other, and the card states r beside the move,
    // so admitting one of those prints a positive number under advice that says
    // the opposite.
    if (direction !== spec.expect)
        return null;
    if ((r > 0 ? "higher" : "lower") !== spec.expect)
        return null;
    const splitLabel = spec.format(split, units);
    const now = nowFrom(spec, standing, split, todayKey, units);
    return {
        id: spec.id,
        claim: spec.claim,
        shortClaim: spec.shortClaim,
        low: {
            label: spec.lowLabel.replace("{value}", splitLabel),
            value: spec.formatOutcome(lowMean),
            days: low.length,
        },
        high: {
            label: spec.highLabel.replace("{value}", splitLabel),
            value: spec.formatOutcome(highMean),
            days: high.length,
        },
        difference: spec.formatDifference(highMean - lowMean),
        magnitude: (spec.formatMagnitude ?? spec.formatOutcome)(highMean - lowMean),
        interval: intervalOf(spec, low.map((p) => p.y), high.map((p) => p.y)),
        days: pairs.length,
        r,
        impact: relativeGap(lowMean, highMean),
        needs: [...needs],
        reads: [...reads],
        note: spec.note,
        // The card says one of these, never both: what to change, or that there is
        // nothing to change.
        move: ((now?.applies ?? true) ? spec.move : spec.holding)({
            split: splitLabel,
            under: spec.formatOutcome(lowMean),
            over: spec.formatOutcome(highMean),
            difference: spec.formatDifference(highMean - lowMean),
        }),
        now,
        drill: spec.drill,
        cadence: spec.cadence,
        exposure: spec.exposure,
    };
}
/** Which of two pairings on one reading the member is better served by. */
function beats(next, held) {
    const asking = snapshotFindingImproves(next);
    if (asking !== snapshotFindingImproves(held))
        return asking;
    return Math.abs(next.r) > Math.abs(held.r);
}
/** Whether this finding is still asking the member for something. */
export function snapshotFindingImproves(finding) {
    return finding.now?.applies === true;
}
/** The same findings, split the two ways the page reads them. */
export function splitSnapshotFindings(findings) {
    const byExposure = new Map();
    for (const f of findings) {
        if (!f.now)
            continue;
        const held = byExposure.get(f.exposure);
        if (!held || beats(f, held))
            byExposure.set(f.exposure, f);
    }
    const improve = [];
    const going = [];
    for (const f of byExposure.values()) {
        if (snapshotFindingImproves(f))
            improve.push(f);
        else
            going.push(f);
    }
    return { improve, going };
}
const steps = (v) => Math.round(v).toLocaleString();
const hours = (v) => `${Math.round(v * 10) / 10}h`;
const ms = (v) => `${Math.round(v)} ms`;
const bpm = (v) => `${Math.round(v)} bpm`;
const minutes = (v) => `${Math.round(v)} min`;
/** Minutes on a clock once there are enough of them to read as hours. */
const clockMinutes = (v) => {
    const total = Math.round(v);
    if (total < 90)
        return `${total} min`;
    const h = Math.floor(total / 60);
    const m = total % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
};
const degC = (v, units) => formatTemperatureC(v, null, units ?? null);
const moreOrFewer = (unit, d, more = "more", less = "fewer") => `${Math.abs(Math.round(d)).toLocaleString()} ${unit} ${d >= 0 ? more : less}`;
/** How each series reads, and how a difference in it reads. One place per series. */
const SERIES_FORMAT = {
    steps: {
        value: steps,
        delta: (d) => moreOrFewer("steps", d),
        magnitude: (v) => `${Math.abs(Math.round(v)).toLocaleString()} steps`,
        armSuffix: " steps",
    },
    sleepHours: {
        value: hours,
        delta: (d) => `${Math.abs(Math.round(d * 60))} min ${d >= 0 ? "more" : "less"} sleep`,
        magnitude: (v) => `${Math.abs(Math.round(v * 60))} min`,
    },
    deepHours: {
        value: hours,
        delta: (d) => `${Math.abs(Math.round(d * 60))} min ${d >= 0 ? "more" : "less"} deep sleep`,
        magnitude: (v) => `${Math.abs(Math.round(v * 60))} min`,
    },
    wakeDrift: {
        value: minutes,
        delta: (d) => `${Math.abs(Math.round(d))} min ${d >= 0 ? "further from" : "closer to"} your usual`,
        magnitude: (v) => `${Math.abs(Math.round(v))} min`,
        // `minutes` already carries the unit, so the suffix must not repeat it.
        armSuffix: " off",
    },
    hrv: {
        value: ms,
        delta: (d) => `${Math.abs(Math.round(d))} ms ${d >= 0 ? "higher" : "lower"}`,
        magnitude: (v) => `${Math.abs(Math.round(v))} ms`,
    },
    rhr: {
        value: bpm,
        delta: (d) => `${Math.abs(Math.round(d))} bpm ${d >= 0 ? "higher" : "lower"}`,
        magnitude: (v) => `${Math.abs(Math.round(v))} bpm`,
    },
    stressMinutes: {
        value: minutes,
        delta: (d) => `${Math.abs(Math.round(d))} min ${d >= 0 ? "more" : "less"} in stress`,
        magnitude: (v) => `${Math.abs(Math.round(v))} min`,
    },
    activeMinutes: {
        value: minutes,
        delta: (d) => moreOrFewer("active minutes", d),
        magnitude: (v) => `${Math.abs(Math.round(v))} min`,
    },
    deskMinutes: {
        value: minutes,
        delta: (d) => moreOrFewer("minutes", d),
        magnitude: (v) => `${Math.abs(Math.round(v))} min`,
    },
    sedentaryMinutes: {
        // Hours, because a day of sitting is read in hours and nobody pictures 640
        // minutes. The split still names the boundary in the same unit.
        value: (v) => `${(v / 60).toFixed(1)}h`,
        delta: (d) => `${Math.abs(d / 60).toFixed(1)}h ${d >= 0 ? "more" : "less"} sitting`,
        magnitude: (v) => `${Math.abs(v / 60).toFixed(1)}h`,
    },
    calendarMinutes: {
        // The claim says hours, so the split and the arms say hours too.
        value: clockMinutes,
        delta: (d) => moreOrFewer("minutes", d),
        magnitude: (v) => `${Math.abs(Math.round(v))} min`,
        armSuffix: " of meetings",
    },
    rainMm: {
        // Only ever an exposure, and one that splits on a threshold rather than a
        // median, so nothing reads the arm means or the gap in millimetres.
        value: (v) => `${v.toFixed(1)} mm`,
        delta: (d) => `${Math.abs(d).toFixed(1)} mm`,
        magnitude: (v) => `${Math.abs(v).toFixed(1)} mm`,
    },
    nightTempC: {
        value: degC,
        delta: (d) => `${Math.abs(Math.round(d))} °C`,
        magnitude: (v) => `${Math.abs(Math.round(v))} °C`,
    },
};
const FINDING_ENTRIES = [
    {
        id: "steps_sleep",
        shortClaim: "Steps to sleep",
        claim: "Steps and how long you sleep that night",
        exposure: "steps",
        outcome: "sleepHours",
        lag: 1,
        note: "Your steps come from your phone or watch; your sleep from whatever measured the night.",
        move: (m) => `Aim for ${m.split} steps, most of them before the evening.`,
        holding: (m) => `You are walking more than ${m.split} most days, and sleeping longer those nights.`,
        whenLabel: (v) => `${v} steps`,
        cadence: "day",
        expect: "higher",
    },
    {
        id: "sleep_steps",
        shortClaim: "Sleep to steps",
        claim: "How long you sleep and the next day's steps",
        exposure: "sleepHours",
        outcome: "steps",
        lag: 1,
        note: "Both readings are your own, paired night to next day.",
        move: (m) => `After a night under ${m.split}, plan a lighter day in the morning.`,
        holding: (m) => `You are sleeping more than ${m.split} most nights, and walking more the next day.`,
        whenLabel: (v) => `${v} asleep`,
        cadence: "night",
        expect: "higher",
    },
    {
        // Regularity predicts mortality better than duration does (Windred 2024,
        // 60,977 UK Biobank participants), and it is the one sleep lever a member
        // controls directly: the hour they get up does not depend on how well they
        // slept. Paired against recovery, which is what they can feel.
        id: "drift_hrv",
        shortClaim: "Wake time to HRV",
        claim: "How close you wake to your usual hour and your heart rate variability",
        exposure: "wakeDrift",
        outcome: "hrv",
        lag: 0,
        note: "Measured on the nights your device reported both.",
        move: (m) => `Get up within ${m.split} of your usual time, including after a bad night.`,
        holding: (m) => `You are waking within ${m.split} of your usual time most days.`,
        whenLabel: (v) => `${v} from your usual wake time`,
        cadence: "night",
        expect: "lower",
    },
    {
        id: "sleep_hrv",
        shortClaim: "Sleep to HRV",
        claim: "How long you sleep and your heart rate variability",
        exposure: "sleepHours",
        outcome: "hrv",
        lag: 0,
        note: "Measured on the nights your device reported both.",
        move: (m) => `Go to bed earlier, aiming for more than ${m.split} a night.`,
        holding: (m) => `You are sleeping more than ${m.split} most nights, and your recovery is better on those nights.`,
        whenLabel: (v) => `${v} asleep`,
        cadence: "night",
        expect: "higher",
    },
    {
        id: "sleep_rhr",
        shortClaim: "Sleep to resting rate",
        claim: "How long you sleep and your resting heart rate",
        exposure: "sleepHours",
        outcome: "rhr",
        lag: 0,
        note: "Measured on the nights your device reported both.",
        move: (m) => `Sleep more than ${m.split}. A high morning rate is a sign to take the day easier.`,
        holding: (m) => `You are sleeping more than ${m.split} most nights, and your resting heart rate is lower.`,
        whenLabel: (v) => `${v} asleep`,
        cadence: "night",
        expect: "lower",
    },
    {
        // The device's own read of sitting, which needs no desk connector to fire.
        // Sedentary time sits with step volume and sleep duration in the strongest
        // evidence tier, and it was arriving in the payload unmapped.
        id: "sitting_hrv",
        shortClaim: "Sitting to HRV",
        claim: "How long you spend sitting and the next morning's heart rate variability",
        exposure: "sedentaryMinutes",
        outcome: "hrv",
        // Overnight HRV is measured before most of a day's sitting has happened,
        // so same-day pairing judged the sitting against a reading that preceded
        // it. Desk time, the same shape, was already paired a day forward.
        lag: 1,
        note: "Sitting time your device reported, with sleep and other rest counted separately.",
        move: (m) => `Keep sitting under ${m.split} a day, and get up often.`,
        holding: (m) => `You are sitting less than ${m.split} most days.`,
        whenLabel: (v) => `${v} sitting`,
        cadence: "day",
        expect: "lower",
    },
    {
        id: "desk_hrv",
        shortClaim: "Desk to HRV",
        claim: "Time at your desk and the next morning's heart rate variability",
        exposure: "deskMinutes",
        outcome: "hrv",
        lag: 1,
        note: "Desk time comes from your browser or your work chat; heart rate variability from your wearable.",
        move: () => `Stand up once an hour while you are at your desk.`,
        holding: (m) => `You are at your desk less than ${m.split} most days.`,
        whenLabel: (v) => `${v} at your desk`,
        cadence: "day",
        expect: "lower",
    },
    {
        id: "calendar_steps",
        shortClaim: "Meetings to steps",
        claim: "Meetings in your calendar and that day's steps",
        exposure: "calendarMinutes",
        outcome: "steps",
        lag: 0,
        note: "Only the timed events in your calendar count, never their titles.",
        move: (m) => `On days with more than ${m.split} of meetings, take one call while walking.`,
        holding: (m) => `You are keeping meetings under ${m.split} most days, and walking more on those days.`,
        whenLabel: (v) => `${v} of meetings`,
        cadence: "day",
        expect: "lower",
    },
    {
        // The one no wrist-worn device can make: it does not know the weather.
        id: "warm_deep",
        shortClaim: "Warm nights to deep",
        claim: "How warm the night was and your deep sleep",
        exposure: "nightTempC",
        outcome: "deepHours",
        lag: 0,
        note: "Overnight lows come from Open-Meteo for where you are; your sleep from your own device.",
        move: (m) => `Cool your bedroom before bed when the night will be over ${m.split}.`,
        holding: (m) => `Your room is under ${m.split} most nights, and your deep sleep is better then.`,
        whenLabel: (v) => `${v} in your room`,
        cadence: "night",
        expect: "lower",
    },
    {
        id: "steps_deep",
        shortClaim: "Steps to deep sleep",
        claim: "Steps and your deep sleep that night",
        exposure: "steps",
        outcome: "deepHours",
        lag: 1,
        note: "A hard day usually changes deep sleep before the other stages.",
        move: (m) => `Aim for ${m.split} steps on the days you want a deeper night.`,
        holding: (m) => `You are walking more than ${m.split} most days, and getting more deep sleep those nights.`,
        whenLabel: (v) => `${v} steps`,
        cadence: "day",
        expect: "higher",
    },
    {
        id: "active_sleep",
        shortClaim: "Active to sleep",
        claim: "Active minutes and how long you sleep that night",
        exposure: "activeMinutes",
        outcome: "sleepHours",
        lag: 1,
        note: "Active minutes count effort your step total does not, like a ride or a swim.",
        move: (m) => `Get ${m.split} of activity, earlier in the day where you can.`,
        holding: (m) => `You are active for more than ${m.split} most days, and sleeping longer those nights.`,
        whenLabel: (v) => `${v} active`,
        cadence: "day",
        expect: "higher",
    },
    {
        id: "sleep_stress",
        shortClaim: "Sleep to stress",
        claim: "How long you sleep and the minutes you spend in stress the next day",
        exposure: "sleepHours",
        outcome: "stressMinutes",
        lag: 1,
        note: "Your ring counts the minutes it reads you as stressed.",
        move: (m) => `Sleep more than ${m.split} the night before a demanding day.`,
        holding: (m) => `You are sleeping more than ${m.split} most nights, and calmer the next day.`,
        whenLabel: (v) => `${v} asleep`,
        cadence: "night",
        expect: "lower",
    },
    {
        /*
         * Weather needs no device and no waiting: the archive lands ninety days
         * deep on the first write, so this clears the day threshold as soon as a
         * member has a location. The exposure is not theirs to choose, which is the
         * point. The move is the response to it.
         */
        id: "rain_steps",
        shortClaim: "Rain to steps",
        claim: "Rain where you were and that day's steps",
        exposure: "rainMm",
        outcome: "steps",
        lag: 0,
        // 0.2 mm is where a met service stops calling it a trace and starts calling
        // it rain, which is also about where a person stops and looks for a coat.
        split: { at: 0.2, low: "Dry days", high: "Wet days" },
        note: "Rainfall for your location, from the weather service rather than your device.",
        move: () => "Keep an indoor option you like, for the days it is wet.",
        holding: () => "Wet days are not stopping you.",
        whenLabel: (v) => `${v} of rain`,
        cadence: "day",
        expect: "lower",
    },
    {
        id: "deep_hrv",
        shortClaim: "Deep sleep to HRV",
        claim: "Your deep sleep and your heart rate variability that night",
        exposure: "deepHours",
        outcome: "hrv",
        lag: 0,
        note: "Both are measured across the same night by the same device.",
        move: () => `Go to bed earlier. Most deep sleep happens early in the night.`,
        holding: (m) => `You are getting more than ${m.split} of deep sleep most nights.`,
        whenLabel: (v) => `${v} of deep sleep`,
        cadence: "night",
        expect: "higher",
    },
];
/*
 * Every formatter a spec needs is a property of the series it reads, so the
 * spec names the two series and the rest follows. Written out per spec, the
 * same four formatters appeared in eleven places and two specs sharing an
 * outcome could drift apart.
 */
export const SNAPSHOT_FINDING_SPECS = FINDING_ENTRIES.map((entry) => {
    const exposure = SERIES_FORMAT[entry.exposure];
    const outcome = SERIES_FORMAT[entry.outcome];
    const arm = exposure.armSuffix ?? "";
    return {
        spec: {
            id: entry.id,
            exposure: entry.exposure,
            claim: entry.claim,
            shortClaim: entry.shortClaim,
            lowLabel: entry.split ? entry.split.low : `Under {value}${arm}`,
            highLabel: entry.split ? entry.split.high : `Over {value}${arm}`,
            ...(entry.split ? { splitAt: entry.split.at } : {}),
            format: exposure.value,
            formatOutcome: outcome.value,
            formatDifference: outcome.delta,
            formatMagnitude: outcome.magnitude,
            note: entry.note,
            move: entry.move,
            whenLabel: entry.whenLabel,
            cadence: entry.cadence,
            holding: entry.holding,
            expect: entry.expect,
            drill: SERIES_DRILL[entry.outcome] ?? "recovery",
        },
        exposure: entry.exposure,
        outcome: entry.outcome,
        lag: entry.lag,
    };
});
function variance(values, avg) {
    if (values.length < 2)
        return 0;
    return values.reduce((sum, v) => sum + (v - avg) ** 2, 0) / (values.length - 1);
}
/*
 * The 95% interval on the gap between the two arms, Welch's form, since the
 * arms are neither paired nor the same size. Stating the gap without it invites
 * a reader to treat one week of noise as a result.
 */
function intervalOf(spec, low, high) {
    if (low.length < 2 || high.length < 2)
        return null;
    const lowMean = mean(low);
    const highMean = mean(high);
    const se = Math.sqrt(variance(low, lowMean) / low.length + variance(high, highMean) / high.length);
    if (!Number.isFinite(se) || se === 0)
        return null;
    const delta = highMean - lowMean;
    const half = 1.96 * se;
    const lo = Math.abs(delta) - half;
    const hi = Math.abs(delta) + half;
    if (lo <= 0)
        return null;
    const fmt = spec.formatMagnitude ?? spec.formatOutcome;
    return `${fmt(lo)} to ${fmt(hi)}`;
}
/** What the line beside a carried finding says, for both clients. */
export function snapshotFindingCarriedSay(finding) {
    const when = finding.cadence === "night" ? "tonight" : "today";
    return `Heartbeat is paying attention to this ${when}`;
}
/** Strongest first. Three surfaces sorted by impact; they now sort the same. */
export function rankSnapshotFindings(findings) {
    return [...findings].sort((a, b) => (b.impact ?? 0) - (a.impact ?? 0));
}
/** The headings and hints both clients put above the two lists. */
export const SNAPSHOT_FINDING_SECTIONS = {
    improve: { heading: "Areas to improve", hint: "Heartbeat is watching for these" },
    going: { heading: "What's going well", hint: "Measured across your own days" },
};
//# sourceMappingURL=snapshot-findings.js.map